import * as CL from '@design-system/component-library';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Link } from 'react-router';
import { dsClass } from '../../../common/constants/dsClasses.js';
import { paths } from '../../../common/constants/pathVariables.js';
import { useConductrics } from '../../../common/hooks/useConductrics.js';
import { marketingConsentMsg, t } from '../../../common/i18n/index.js';
import {
  FORM_GRID_FULL_WIDTH,
  FormGridFieldset,
} from '../../../common/react-hook-form/FormGridFieldset/FormGridFieldset.js';
import { Checkbox } from '../../../common/react-hook-form/components/Checkbox.js';
import { InputComponent } from '../../../common/react-hook-form/components/InputComponent.js';
import { CompanySearch } from '../../../common/react-hook-form/fields/CompanySearch.js';
import { Email } from '../../../common/react-hook-form/fields/Email.js';
import { PhoneNumber } from '../../../common/react-hook-form/fields/PhoneNumber.js';
import { CustomFormErrorAndButton } from '../CustomFormFields.js';
import { PrivacyStatement } from './PrivacyStatement.js';
import type { CustomFormProps } from '../CustomForm.js';

import './KukaSoittaaLeadForm.scss';

const KukaSoittaaHeader = () => (
  <div className="of-kukasoittaa-lead-form__header">
    <h2>{t.EER5('Order Elisa Kuka Soittaa service')}</h2>
    <p>{t.GOK4('Order Elisa Kuka Soittaa service from Business OmaElisa or through our expert.')}</p>
  </div>
);

const AGENT_CODE = 'a-bZ4NvQMrNP9Z';
const GOAL_CODE = 'kukasoittaa_order_from_noe_click';

const OrderFromOmaelisa = () => {
  const { getVariation, sendGoalEvent, isLoaded: conductricsLoaded } = useConductrics();
  const [variation, setVariation] = useState<'A' | 'B'>('A');

  useEffect(() => {
    const fetchVariation = async () => {
      const response = await getVariation<'A' | 'B'>(AGENT_CODE);
      setVariation(response);
    };

    if (conductricsLoaded) {
      fetchVariation();
    }
  }, [conductricsLoaded, getVariation]);

  const handleClick = () => {
    if (conductricsLoaded) {
      sendGoalEvent(GOAL_CODE);
    }
  };

  return (
    <div className="of-kukasoittaa-lead-form__oma-elisa-section">
      <div className="of-kukasoittaa-lead-form__icon">
        <svg className="of-kukasoittaa-lead-form__self-service-icon" />
      </div>
      <h3>{t.HY0C('Order from Business OmaElisa')}</h3>
      <p>
        {variation === 'A' && (
          <>
            {t.TCDE(
              'You can easily order the Kuka Soittaa service from Business OmaElisa if your company has Elisa business subscriptions.'
            )}
          </>
        )}
        {variation === 'B' && (
          <>
            {t.DIEL(
              'Order Elisa Kuka Soittaa service effortlessly for one or more business subscriptions in Business OmaElisa.'
            )}
          </>
        )}
      </p>
      <Link
        onClick={handleClick}
        to={paths.PS_MOBILE_SUBSCRIPTION_MULTISUB_ADDONS}
        className="ds-link ds-link--style-link-button ds-link--button-color-primary ds-link--button-size-l"
      >
        {t.UK6U('Order from OmaElisa')}
      </Link>
    </div>
  );
};

interface OrderFromCustomerServiceProps {
  leadFailed?: boolean;
  leadInProgress?: boolean;
  leadSent?: boolean;
}

interface KukaSoittaaThankYouProps {
  heading: string;
  text: string;
}

const KukaSoittaaThankYou = ({ heading, text }: KukaSoittaaThankYouProps) => (
  <div className="of-kukasoittaa-lead-form__thank-you">
    <div className={classNames(dsClass.DISPLAY_FLEX, dsClass.ALIGN_ITEMS_CENTER, dsClass.MARGIN_BOTTOM_3)}>
      <CL.Icon color="green-600" icon="check" size="xl" type="filled" />
      <h2 className={dsClass.MARGIN_LEFT_2}>{heading}</h2>
    </div>
    <p>{text}</p>
  </div>
);

export const OrderFromCustomerService = ({ leadFailed, leadInProgress, leadSent }: OrderFromCustomerServiceProps) => {
  const { setValue } = useFormContext();

  const LEAD_MESSAGE = `Hei, asiakas on jättänyt yhteydenottopyynnön "Kuka soittaa" -palvelusivumme kautta kertoen haluavansa keskustella myyjän kanssa ennen tilauksen tekemistä. Olethan pikaisesti asiakkaaseen yhteydessä, kiitos.`;
  const DEFAULT_FIRST_NAME = 'default_name';
  const DEFAULT_LAST_NAME = 'default_last_name';

  useEffect(() => {
    setValue('message', LEAD_MESSAGE);
    setValue('firstName', DEFAULT_FIRST_NAME);
    setValue('lastName', DEFAULT_LAST_NAME);
  }, [LEAD_MESSAGE, DEFAULT_FIRST_NAME, DEFAULT_LAST_NAME, setValue]);

  return (
    <>
      <svg className="of-kukasoittaa-lead-form__support-icon" />
      <h3>{t.AQOM('Order from an expert')}</h3>
      <p>
        {t.ULHB(
          'If you would like to speak with our expert before ordering, please leave us a contact request. We will contact you within one business day.'
        )}
      </p>
      {leadSent ? (
        <KukaSoittaaThankYou
          heading={t.O4R6('Thank you for your order!')}
          text={t.WAS5('Thank you! We will contact you within one business day.')}
        />
      ) : (
        <>
          <FormGridFieldset>
            <InputComponent type="hidden" name="message" disabled={false} required={false} validate={() => undefined} />
            <InputComponent
              type="hidden"
              name="firstName"
              disabled={false}
              required={false}
              validate={() => undefined}
            />
            <InputComponent
              type="hidden"
              name="lastName"
              disabled={false}
              required={false}
              validate={() => undefined}
            />
            <div className={FORM_GRID_FULL_WIDTH}>
              <CompanySearch placeholder="" />
            </div>
            <Email placeholder="" />

            <PhoneNumber placeholder="" />
            <Checkbox className={FORM_GRID_FULL_WIDTH} label={t.GZUL(marketingConsentMsg)} name="consentForMarketing" />
            <PrivacyStatement />
          </FormGridFieldset>
          <CustomFormErrorAndButton
            buttonLabel={t.AQOM('Order from an expert')}
            leadFailed={leadFailed}
            leadInProgress={leadInProgress}
          />
        </>
      )}
    </>
  );
};

export const KukaSoittaaLeadForm = ({ leadFailed, leadInProgress, leadSent }: CustomFormProps) => {
  return (
    <CL.Grid className="of-kukasoittaa-lead-form">
      <CL.GridRow justifyCenter>
        <CL.GridCol colWidthS={6} colWidthL={10} colWidthXL={10}>
          <div className="of-kukasoittaa-lead-form__content-wrapper">
            <KukaSoittaaHeader />
            <CL.Grid>
              <CL.GridRow>
                <CL.GridCol colWidthS={6} colWidthL={4} colWidthXL={4}>
                  <OrderFromOmaelisa />
                </CL.GridCol>
                <CL.GridCol colWidthS={6} colWidthL={8} colWidthXL={8}>
                  <div className="of-kukasoittaa-lead-form__expert-section">
                    <OrderFromCustomerService
                      leadFailed={leadFailed}
                      leadInProgress={leadInProgress}
                      leadSent={leadSent}
                    />
                  </div>
                </CL.GridCol>
              </CL.GridRow>
            </CL.Grid>
          </div>
        </CL.GridCol>
      </CL.GridRow>
    </CL.Grid>
  );
};
